// Admin v2 — Közösségi oldalak moderálása (app: community_pages, v6/72-73, v8/92).
// Lista: név, tulajdonos, követők, bejegyzések, létrehozás; műveletek: Hivatalos jelvény,
// elrejtés (hidden — csak a tulajdonos látja, a hírfal is kihagyja a bejegyzéseit), törlés.
import React, { useEffect, useMemo, useState } from 'react';
import {
  collection, deleteDoc, doc, getDoc, getDocs, onSnapshot, query, updateDoc, where, writeBatch,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { fmtInt, toDate } from '../data';
import { Avatar, Badge, ConfirmModal, Empty, Input, PageHeader, Segmented, Spinner, Toggle, useToast } from '../ui';
import { Icon } from '../icons';

interface Page {
  id: string;
  name: string;
  city: string;
  desc: string;
  avatarUrl: string | null;
  ownerUid: string;
  ownerName: string;
  followers: number;
  official: boolean;
  hidden: boolean;
  createdAt: Date | null;
}

type Filter = 'all' | 'visible' | 'hidden' | 'official';
const APP_URL = 'https://dartiqon-app.web.app';

export default function CommunityPagesPage() {
  const toast = useToast();
  const [pages, setPages] = useState<Page[] | null>(null);
  const [owners, setOwners] = useState<Record<string, string>>({});
  const [postCounts, setPostCounts] = useState<Record<string, number>>({});
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [deleting, setDeleting] = useState<Page | null>(null);

  useEffect(() => onSnapshot(collection(db, 'community_pages'), (snap) => {
    setPages(snap.docs.map((d) => {
      const v = d.data();
      return {
        id: d.id,
        name: String(v.name ?? 'Névtelen oldal'),
        city: String(v.city ?? ''),
        desc: String(v.desc ?? ''),
        avatarUrl: (v.avatarUrl as string) ?? null,
        ownerUid: String(v.ownerUid ?? ''),
        ownerName: '',
        followers: Array.isArray(v.followerIds) ? v.followerIds.length : 0,
        official: Boolean(v.official),
        hidden: Boolean(v.hidden),
        createdAt: toDate(v.createdAt),
      };
    }).sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0)));
  }, () => setPages([])), []);

  // tulajdonosok neve + bejegyzésszám (új posts kollekció communityId-vel + régi alkollekció)
  useEffect(() => {
    if (!pages) return;
    const missing = Array.from(new Set(pages.map((p) => p.ownerUid))).filter((u) => u && !(u in owners));
    if (missing.length) {
      void Promise.all(missing.map(async (u) => {
        const s = await getDoc(doc(db, 'users', u)).catch(() => null);
        const v = s?.exists() ? s.data() : null;
        return [u, v ? String(v.username || v.displayName || v.email || u) : '—'] as const;
      })).then((rows) => setOwners((o) => ({ ...o, ...Object.fromEntries(rows) })));
    }
    void Promise.all(pages.map(async (p) => {
      const [a, b] = await Promise.all([
        getDocs(query(collection(db, 'posts'), where('communityId', '==', p.id))).then((s) => s.size).catch(() => 0),
        getDocs(collection(db, 'community_pages', p.id, 'posts')).then((s) => s.size).catch(() => 0),
      ]);
      return [p.id, a + b] as const;
    })).then((rows) => setPostCounts(Object.fromEntries(rows)));
  }, [pages?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (pages ?? [])
      .filter((p) => filter === 'all' || (filter === 'hidden' ? p.hidden : filter === 'visible' ? !p.hidden : p.official))
      .filter((p) => !s || [p.name, p.city, owners[p.ownerUid] ?? ''].some((f) => f.toLowerCase().includes(s)));
  }, [pages, q, filter, owners]);

  const set = async (p: Page, field: 'official' | 'hidden', value: boolean) => {
    try {
      await updateDoc(doc(db, 'community_pages', p.id), { [field]: value });
      toast(field === 'hidden' ? (value ? 'Oldal elrejtve.' : 'Oldal újra látható.') : (value ? 'Hivatalos jelvény bekapcsolva.' : 'Hivatalos jelvény kikapcsolva.'));
    } catch {
      toast('A módosítás nem sikerült (Firestore-szabályok deployolva?).', 'red');
    }
  };

  const remove = async (p: Page) => {
    try {
      // az oldal bejegyzései: új (posts + communityId) és régi (alkollekció), majd maga az oldal
      const [newPosts, oldPosts] = await Promise.all([
        getDocs(query(collection(db, 'posts'), where('communityId', '==', p.id))),
        getDocs(collection(db, 'community_pages', p.id, 'posts')).catch(() => null),
      ]);
      const refs = [...newPosts.docs.map((d) => d.ref), ...(oldPosts?.docs.map((d) => d.ref) ?? [])];
      for (let i = 0; i < refs.length; i += 400) {
        const b = writeBatch(db);
        refs.slice(i, i + 400).forEach((r) => b.delete(r));
        await b.commit();
      }
      await deleteDoc(doc(db, 'community_pages', p.id));
      toast(`Oldal törölve (${refs.length} bejegyzéssel).`);
    } catch (e) {
      console.warn('[community] törlés', e);
      toast('A törlés nem sikerült.', 'red');
    }
  };

  const hiddenCount = (pages ?? []).filter((p) => p.hidden).length;

  return (
    <div>
      <PageHeader title="Közösségi oldalak" sub="A felhasználók által létrehozott közösségi oldalak moderálása" />

      <Input icon="MagnifyingGlass" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keresés név, város vagy tulajdonos alapján..." />
      <div className="mt-4">
        <Segmented<Filter> value={filter} onChange={setFilter} options={[
          { value: 'all', label: `Összes${pages ? ` (${pages.length})` : ''}` },
          { value: 'visible', label: 'Látható' },
          { value: 'hidden', label: `Elrejtett${hiddenCount ? ` (${hiddenCount})` : ''}` },
          { value: 'official', label: 'Hivatalos' },
        ]} />
      </div>

      <div className="mt-6 bg-[#15191E] border border-[#31383D] rounded-2xl overflow-hidden">
        {pages === null ? <Spinner /> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] text-left">
              <thead>
                <tr className="bg-[#1d232a] text-[12px] uppercase text-[#8a8f99]">
                  <th className="pl-5 pr-4 h-[39px] font-semibold w-[28%]">Oldal</th>
                  <th className="px-4 font-semibold w-[16%]">Tulajdonos</th>
                  <th className="px-4 font-semibold w-[9%]">Követők</th>
                  <th className="px-4 font-semibold w-[10%]">Bejegyzések</th>
                  <th className="px-4 font-semibold w-[11%]">Létrehozva</th>
                  <th className="px-4 font-semibold w-[9%]" title="Hivatalos Dartiqon közösség jelvény">Hivatalos</th>
                  <th className="px-4 font-semibold w-[9%]" title="Ki: csak a tulajdonos látja, a hírfal sem mutatja">Látható</th>
                  <th className="px-4 font-semibold w-[8%]">Műveletek</th>
                </tr>
              </thead>
              <tbody>
                {list.map((p) => (
                  <tr key={p.id} className={`border-t border-[#31383D] h-[63px] ${p.hidden ? 'opacity-60' : ''}`}>
                    <td className="pl-5 pr-4">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Avatar src={p.avatarUrl} name={p.name} size={34} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-[14px] font-semibold text-white truncate">{p.name}</span>
                            {p.hidden && <Badge tone="red">Elrejtve</Badge>}
                          </div>
                          <span className="block text-[12px] text-[#8a8f99] truncate max-w-[300px]">{[p.city, p.desc].filter(Boolean).join(' · ') || '—'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 text-[14px] text-[#B8B8C3] truncate">{owners[p.ownerUid] ?? '…'}</td>
                    <td className="px-4 text-[14px] text-[#B8B8C3]">{fmtInt(p.followers)}</td>
                    <td className="px-4 text-[14px] text-[#B8B8C3]">{p.id in postCounts ? fmtInt(postCounts[p.id]) : '…'}</td>
                    <td className="px-4 text-[13px] text-[#B8B8C3]">{p.createdAt ? p.createdAt.toLocaleDateString('hu-HU') : '—'}</td>
                    <td className="px-4"><Toggle checked={p.official} onChange={(v) => void set(p, 'official', v)} /></td>
                    <td className="px-4"><Toggle checked={!p.hidden} onChange={(v) => void set(p, 'hidden', !v)} /></td>
                    <td className="px-4">
                      <div className="flex items-center gap-2">
                        <a href={`${APP_URL}/community/${p.id}`} target="_blank" rel="noreferrer" title="Megnyitás az appban"
                          className="inline-flex items-center justify-center w-[34px] h-[34px] rounded-[10px] bg-[#1d232a] border border-[#31383D] text-[#B8B8C3] hover:border-[#00AFF2]/50">
                          <Icon name="Eye" size={16} />
                        </a>
                        <button type="button" title="Törlés" aria-label="Törlés" onClick={() => setDeleting(p)}
                          className="inline-flex items-center justify-center w-[34px] h-[34px] rounded-[10px] bg-[#1d232a] border border-[#31383D] text-[#ef4444] hover:border-[#ef4444]/50">
                          <Icon name="Trash" size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {list.length === 0 && <Empty>{pages.length ? 'Nincs a szűrésnek megfelelő oldal.' : 'Még nincs közösségi oldal.'}</Empty>}
          </div>
        )}
      </div>

      {deleting && (
        <ConfirmModal
          title="Közösségi oldal törlése"
          text={<>Biztosan törlöd a(z) <b className="text-white">{deleting.name}</b> oldalt és az összes bejegyzését? A művelet nem vonható vissza. Ha csak ideiglenesen kell eltüntetni, használd inkább a „Látható” kapcsolót.</>}
          confirmLabel="Törlés"
          danger
          onClose={() => setDeleting(null)}
          onConfirm={async () => { const p = deleting; setDeleting(null); await remove(p); }}
        />
      )}
    </div>
  );
}
